import { Card, Flex, Tag, Text } from "@chakra-ui/react";
import dayjs from "dayjs";
import { type APIListingOffer } from "models/types/api-listing";
import { CardRecordMeta } from "components/cards/card-record-meta";
import { useCurrencyIcon, useFormatMoney } from "hooks/use-format-money";
import { useRegion } from "hooks/use-region";
import { useWhichWordFactory } from "modules/vendor-portal/hooks/use-which-words";
import { FeedbackIcon } from "modules/vendor-portal/screens/overview/components/feedback/feedback-icon";
import { dateFormats } from "utils/date";
import { upperFirst } from "utils/string";

const colorMap: { [key: string]: string } = {
  accepted: "badgeBlue",
  unconditional: "badgeGreen",
  settled: "badgeGreen",
  offer: "gray",
  fallen: "badgeOrange",
};

const UkColorMap: { [key: string]: string } = {
  communicated: "badgeBlue",
  accepted: "badgeGreen",
  exchanged: "badgeGreen",
  completed: "badgeGreen",
  fallen: "badgeOrange",
  withdrawn: "badgeOrange",
  rejected: "badgeOrange",
  offer: "gray",
};

type OfferListItemProps = {
  offer: APIListingOffer;
};

export function OfferListItem({ offer }: OfferListItemProps) {
  const region = useRegion();
  const ww = useWhichWordFactory();
  const formatMoney = useFormatMoney();
  const currencyIcon = useCurrencyIcon();
  const regionalColorMap = region === "GBR" ? UkColorMap : colorMap;

  const isPrimaryAccepted =
    offer.status.id === "accepted" && !offer.is_backup_offer;
  const isFirstBackup = offer.is_primary_backup_offer;
  const isBackup = offer.is_backup_offer && !offer.is_primary_backup_offer;

  return (
    <Card pt={3} pr={10} pb={4} pl={3} variant={"outline"}>
      <Flex direction={"column"} gap={2} justify={"flex-start"}>
        <Flex direction={"row"} gap={2} justify={"flex-start"}>
          <FeedbackIcon icon={currencyIcon} />

          <Flex direction={"column"} gap={2} flexGrow={1}>
            <Flex
              direction={"row"}
              align={"center"}
              justify={"space-between"}
              gap={2}
            >
              <Text textStyle={"large"} fontWeight={500} color={"gray.700"}>
                {offer.applicants.map((a) => a.name).join(", ")}
              </Text>
              <Text
                textStyle={"large"}
                fontWeight={"semibold"}
                color={"gray.700"}
              >
                {formatMoney(offer.amount_of)}
              </Text>
            </Flex>
            <Flex direction={"column"} gap={3}>
              <CardRecordMeta>
                <Tag
                  colorScheme={regionalColorMap[offer.status.id]}
                  fontSize={"sm"}
                  fontWeight={600}
                  color={`${regionalColorMap[offer.status.id]}.800`}
                  lineHeight={"16px"}
                  py={0.5}
                  px={2}
                  whiteSpace={"nowrap"}
                >
                  {upperFirst(ww(offer.status.id))}
                </Tag>

                {isPrimaryAccepted ? (
                  <Text fontSize={"md"} fontWeight={400}>
                    Active
                  </Text>
                ) : null}

                {isFirstBackup ? (
                  <Text fontSize={"md"} fontWeight={400}>
                    1st backup
                  </Text>
                ) : null}

                {isBackup ? (
                  <Text fontSize={"md"} fontWeight={400}>
                    Backup
                  </Text>
                ) : null}

                {offer.date_withdrawn ? (
                  <Text fontSize={"md"} fontWeight={400}>
                    Withdrawn on{" "}
                    {dayjs(offer.date_withdrawn).format(
                      dateFormats.dayMonthYear,
                    )}
                  </Text>
                ) : (
                  <Text fontSize={"md"} fontWeight={400}>
                    {dayjs(offer.date_of_offer).format(
                      dateFormats.dayMonthYear,
                    )}
                  </Text>
                )}
                {offer.agent ? (
                  <Text fontSize={"md"} fontWeight={400}>
                    Agent {offer.agent.name}
                  </Text>
                ) : null}
              </CardRecordMeta>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
}
